import { Key, useState } from 'react';
import { ISavedViewsHook } from 'utils/hooks/useSavedViews/useSavedViews';

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownSection,
  Flex,
  Icon,
  IconButton,
  TextField,
  Typography,
} from '@project44/manifest-ds';
import { useTranslation } from '@project44/translations-utils';
import { SavedView as TSavedView } from '@project44-models/shipment-list';

const getCopyName = (name: string) => {
  const next = name.substring(0, 39);
  if (next.match(/copy$/)) {
    return next;
  }
  return `${next} copy`;
};

const styles = {
  option: {
    padding: '0 $medium',
    backgroundColor: '$background-top-nav',
    '&:hover': { bgColor: '#415799' },
    '&:hover .viewMenu': { display: 'inherit' },
  },
  text: {
    flex: 1,
    color: '$text-contrast',
    cursor: 'pointer',
    overflow: 'hidden',
    padding: '$medium 0',
    textOverflow: 'ellipsis',
    textWrap: 'nowrap',
  },
  icon: {
    color: '$text-contrast',
    cursor: 'grab',
    fontSize: '$large',
    display: 'none',
    padding: '0 $x-small 0 0',
    userSelect: 'auto',
  },
  viewMenu: {
    color: 'white',
    display: 'none',
    '&:hover': { bgColor: '#5B6AA1' },
  },
};

interface IViewPanelOption {
  fallbackViewId: string;
  handleView: () => void;
  isSelected: boolean;
  savedEntities: ISavedViewsHook;
  setFiltersKey: (key: string, id: string) => void;
  view: TSavedView;
}

function ViewPanelOption({
  fallbackViewId,
  handleView,
  isSelected,
  savedEntities,
  setFiltersKey,
  view,
}: IViewPanelOption) {
  const { t } = useTranslation();
  const [isConfirm, setIsConfirm] = useState(false);
  const [isRename, setIsRename] = useState(false);
  const [rename, setRename] = useState(view.name);

  const onAction = async (view: TSavedView, action: Key) => {
    if (action === 'delete') {
      setIsConfirm(true);
    }
    if (action === 'duplicate') {
      const res = await savedEntities.createView({
        name: getCopyName(view.name),
        filterCriteria: view.filterCriteria,
        displayOptions: view.displayOptions,
      });
      setFiltersKey('listView', res?.views[0]?.id || '');
    }
    if (action === 'rename') {
      setIsRename(true);
    }
  };

  const onRename = async () => {
    setIsRename(false);
    await savedEntities.updateView(view?.id || '', {
      id: view.id,
      name: rename,
      filterCriteria: view.filterCriteria,
      displayOptions: view.displayOptions,
    });
  };

  const onRemove = async () => {
    await savedEntities.deleteView(view?.id || '');
    if (isSelected) {
      setFiltersKey('listView', fallbackViewId);
    }
  };

  if (isRename) {
    return (
      <Flex
        data-testid={view.id}
        align="center"
        css={{
          ...styles.option,
          padding: '$small 0',
        }}
      >
        <TextField
          aria-label={t('core.renameView')}
          inputProps={{ style: { paddingRight: 44 } }}
          onChange={setRename}
          size="small"
          validationState={rename.length <= 40 ? 'valid' : 'invalid'}
          value={rename}
          endIcon={
            <Typography variant="subtext" css={{ fontSize: 12 }}>
              {`${rename.length}/40`}
            </Typography>
          }
        />
        <IconButton
          children={<Icon icon="close" />}
          variant="secondary"
          aria-label={t('core.cancelRenameView')}
          size="small"
          onPress={() => {
            setIsRename(false);
            setRename(view.name);
          }}
        />
        <IconButton
          children={<Icon icon="check" />}
          aria-label={t('core.confirmRenameView')}
          size="small"
          isDisabled={rename.length > 40}
          onPress={onRename}
        />
      </Flex>
    );
  }

  return (
    <Flex
      data-testid={view.id}
      align="center"
      css={{
        ...styles.option,
        bgColor: isSelected ? '#415799' : '',
      }}
      onClick={() => {
        if (!isSelected) {
          handleView();
        }
      }}
    >
      <Icon
        aria-label={t('core.dragHandleLabel', { name: view.name })}
        className="dragHandle viewMenu"
        icon="drag_indicator"
        css={styles.icon}
      />
      <Typography
        variant={isSelected ? 'subtextBold' : 'subtext'}
        children={view.name}
        css={styles.text}
      />
      <Dropdown>
        <IconButton
          aria-label={t('core.showActionsFor', { name: view.name })}
          children={<Icon icon="more_horiz" />}
          size="small"
          variant="tertiary"
          className="viewMenu"
          css={styles.viewMenu}
        />
        <DropdownMenu onAction={(action: Key) => onAction(view, action)}>
          <DropdownItem
            key="rename"
            aria-label={t('core.renameView')}
            children={t('global.rename')}
          />
          <DropdownItem
            key="duplicate"
            aria-label={t('core.duplicateView')}
            children={t('global.duplicate')}
          />
          <DropdownSection>
            <DropdownItem
              key="delete"
              aria-label={t('core.deleteView')}
              css={{ color: '$text-danger' }}
              children={t('global.delete')}
            />
          </DropdownSection>
        </DropdownMenu>
      </Dropdown>
      <Dialog isOpen={isConfirm} onClose={() => setIsConfirm(false)}>
        <DialogHeader>{t('core.deleteViewWarningTitle')}</DialogHeader>
        <DialogContent>
          <Typography
            as="p"
            variant={'subtext'}
            children={t('core.deleteViewBody')}
            css={{ padding: '$medium 0' }}
          />
          <TextField
            maxLength={40}
            as="input"
            aria-label={t('core.renameView')}
            placeholder={t('core.viewName')}
            css={{ pointerEvents: 'none' }}
            value={rename}
            readOnly
          />
        </DialogContent>
        <DialogFooter>
          <Button
            aria-label={t('global.cancel')}
            variant="secondary"
            onPress={() => setIsConfirm(false)}
            children={t('global.cancel')}
          />
          <Button
            aria-label={t('core.deleteView')}
            variant="danger"
            onPress={onRemove}
            children={t('global.delete')}
          />
        </DialogFooter>
      </Dialog>
    </Flex>
  );
}

export default ViewPanelOption;
